import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, User } from 'lucide-react';

interface Article {
  id: string;
  title: string;
  content: string;
  image_url: string | null;
  created_at: string;
  author: string | null;
  category: string | null;
  excerpt: string | null;
  slug?: string;
}

export default function ArticleGrid({ articles }: { articles: Article[] }) {
  if (!articles || articles.length === 0) {
    return <p className="text-center text-neutral-500">Aucun article à afficher 😕</p>;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.165, 0.84, 0.44, 1] },
    },
  };

  const getCategoryColor = (category?: string | null) => {
    const colors: { [key: string]: string } = {
      'Recrutement': 'bg-blue-100 text-blue-800',
      'Marque employeur': 'bg-pink-100 text-pink-800',
      'RH & Management': 'bg-purple-100 text-purple-800',
      'Onboarding': 'bg-green-100 text-green-800',
      'Culture d\'entreprise': 'bg-yellow-100 text-yellow-800',
      'Innovation RH': 'bg-indigo-100 text-indigo-800',
      'Général': 'bg-neutral-100 text-neutral-800'
    };
    return colors[category || 'Général'] || colors['Général'];
  };

  return (
    <motion.div 
      className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {articles.map((article) => (
        <motion.article
          key={article.id}
          variants={itemVariants}
          className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-200 group cursor-pointer"
          onClick={() => window.location.href = `/ressources/articles/${article.slug || article.id}`}
        >
          {article.image_url && (
            <div className="aspect-video relative overflow-hidden">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                decoding="async"
              />
              {article.category && (
                <div className="absolute top-4 left-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(article.category)}`}>
                    {article.category}
                  </span>
                </div>
              )}
            </div>
          )}
          
          <div className="p-6">
            <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
              {article.title}
            </h3>
            
            <p className="text-sm text-neutral-600 mb-4 line-clamp-3">
              {article.excerpt || article.content}
            </p>
            
            <div className="flex items-center gap-4 text-sm text-neutral-500">
              {article.author && (
                <div className="flex items-center gap-1">
                  <User size={16} />
                  <span>{article.author}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Calendar size={16} />
                <span>
                  {new Date(article.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
        </motion.article>
      ))}
    </motion.div>
  );
}